import { invenioConfig } from "@config";
import PropTypes from "prop-types";
import Qs from "qs";
import React, { Component } from "react";
import {
  InvenioRequestSerializer,
  withState as withSearchState,
} from "react-searchkit";
import { Button, Icon, Dropdown } from "semantic-ui-react";

class ExportDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedFormat: props.supportedFormats?.[0] || "csv",
    };
  }

  handleFormatChange = (e, { value }) => {
    this.setState({ selectedFormat: value });
  };

  handleExportClick = () => {
    const { onExportClick, max } = this.props;
    const { selectedFormat } = this.state;
    onExportClick(selectedFormat, max);
  };

  getFormatOptions = () => {
    const { supportedFormats } = this.props;

    const allFormatOptions = [
      { key: "csv", value: "csv", text: "CSV", icon: "table" },
      { key: "pdf", value: "pdf", text: "PDF", icon: "file pdf" },
      { key: "marc21", value: "marc21", text: "MARC21", icon: "file archive" },
    ];

    if (supportedFormats && Array.isArray(supportedFormats)) {
      return allFormatOptions.filter((option) =>
        supportedFormats.includes(option.value)
      );
    }

    return allFormatOptions;
  };

  render() {
    const { isLoading } = this.props;
    const { selectedFormat } = this.state;

    const formatOptions = this.getFormatOptions();
    const selectedOption = formatOptions.find(
      (opt) => opt.value === selectedFormat
    );

    return (
      <Button.Group>
        <Dropdown
          button
          basic
          floating
          options={formatOptions}
          value={selectedFormat}
          onChange={this.handleFormatChange}
          selectOnBlur={false}
          icon={null}
          trigger={
            <span>
              <Icon
                name={selectedOption?.icon}
                style={{ marginRight: "6px" }}
              />
              {selectedFormat.toUpperCase()}
            </span>
          }
        />
        <Button
          primary
          icon
          size="small"
          labelPosition="left"
          onClick={this.handleExportClick}
          loading={isLoading}
          disabled={isLoading}
        >
          <Icon name="download" />
          Export Results
        </Button>
      </Button.Group>
    );
  }
}

ExportDialog.propTypes = {
  onExportClick: PropTypes.func.isRequired,
  max: PropTypes.number,
  isLoading: PropTypes.bool,
  supportedFormats: PropTypes.arrayOf(PropTypes.string),
};

ExportDialog.defaultProps = {
  max: invenioConfig.APP.EXPORT_MAX_RESULTS,
  isLoading: false,
  supportedFormats: ["csv", "pdf", "marc21"], // Default: all formats
};

class ExportSearchResultsWithState extends Component {
  // Generate appropriate filename based on format
  generateFileName = (format, size) => {
    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:-]/g, "");
    const sizeStr = size === -1 ? "all" : size.toString();

    switch (format) {
      case "marc21":
        return `documents_export_${sizeStr}_${timestamp}.mrc`;
      case "csv":
        return `documents_export_${sizeStr}_${timestamp}.csv`;
      case "pdf":
        return `documents_export_${sizeStr}_${timestamp}.pdf`;
      default:
        return `documents_export_${sizeStr}_${timestamp}.${format}`;
    }
  };

  // Add styling to XML content
  addStylingToXML = (xmlContent) => {
    const cssReference = `<?xml-stylesheet type="text/css" href="data:text/css;charset=utf-8,
    record { 
      font-family: 'Segoe UI', Arial, sans-serif; 
      background: white; 
      padding: 20px; 
      margin: 10px; 
      border: 2px solid #2185d0; 
      border-radius: 8px; 
      display: block; 
    }
    leader { 
      display: block; 
      background: linear-gradient(90deg, #e3f2fd, #bbdefb); 
      padding: 12px; 
      margin-bottom: 15px; 
      border-radius: 4px; 
      font-weight: bold; 
      color: #1565c0;
    }
    leader:before { 
      content: '📋 LEADER: '; 
      font-size: 14px; 
    }
    controlfield { 
      display: block; 
      background: linear-gradient(90deg, #fff3e0, #ffe0b2); 
      padding: 8px 12px; 
      margin: 5px 0; 
      border-radius: 4px; 
      border-left: 4px solid #ff9800;
    }
    controlfield:before { 
      content: '🏷️ ' attr(tag) ': '; 
      color: #f57c00; 
      font-weight: bold; 
    }
    datafield { 
      display: block; 
      background: linear-gradient(90deg, #f3e5f5, #e1bee7); 
      padding: 12px; 
      margin: 8px 0; 
      border-radius: 6px; 
      border-left: 5px solid #9c27b0;
    }
    datafield:before { 
      content: '📚 ' attr(tag) ' [' attr(ind1) '' attr(ind2) ']: '; 
      color: #7b1fa2; 
      font-weight: bold; 
      display: block; 
      margin-bottom: 8px; 
      font-size: 14px;
    }
    subfield { 
      display: inline-block; 
      background: linear-gradient(90deg, #e8f5e8, #c8e6c9); 
      padding: 4px 8px; 
      margin: 3px; 
      border-radius: 4px; 
      border: 1px solid #4caf50;
    }
    subfield:before { 
      content: '💠$' attr(code) ' '; 
      color: #2e7d32; 
      font-weight: bold; 
    }" ?>`;

    // Insert styling after XML declaration
    if (xmlContent.startsWith("<?xml")) {
      const declarationEnd = xmlContent.indexOf("?>") + 2;
      return (
        xmlContent.substring(0, declarationEnd) +
        "\n" +
        cssReference +
        "\n" +
        xmlContent.substring(declarationEnd)
      );
    }

    return (
      '<?xml version="1.0" encoding="UTF-8"?>\n' +
      cssReference +
      "\n" +
      xmlContent
    );
  };

  // Handle styled MARC21 export
  handleStyledMARC21Export = async (currentQueryState, size, exportBaseUrl) => {
    try {
      const newQueryState = { ...currentQueryState, size };
      const requestSerializer = new InvenioRequestSerializer();
      const queryString = requestSerializer.serialize(newQueryState);
      const params = Qs.parse(queryString);
      params["page"] = 1;
      params["format"] = "marcxml";

      const args = Qs.stringify(params, {
        arrayFormat: "repeat",
        encode: false,
      });
      const exportUrl = `${exportBaseUrl}?${args}`;

      const response = await fetch(exportUrl, {
        credentials: "include",
        headers: { Accept: "application/marcxml+xml" },
      });

      const xmlText = await response.text();
      const styledXML = this.addStylingToXML(xmlText);

      const blob = new Blob([styledXML], { type: "text/xml;charset=utf-8" });
      const fileName = this.generateFileName("marcxml-styled", size);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Styled MARC21 Export Error:", error);
    }
  };

  // Handle MARC21 export formats with proper file download
  handleMARC21Export = async (
    format,
    size,
    currentQueryState,
    exportBaseUrl
  ) => {
    try {
      const newQueryState = { ...currentQueryState, size };
      const requestSerializer = new InvenioRequestSerializer();
      const queryString = requestSerializer.serialize(newQueryState);
      const params = Qs.parse(queryString);
      params["page"] = 1;
      params["format"] = format;

      const args = Qs.stringify(params, {
        arrayFormat: "repeat",
        encode: false,
      });
      const exportUrl = `${exportBaseUrl}?${args}`;

      // For MARC21 formats, we'll download the file directly
      const response = await fetch(exportUrl, {
        credentials: "include",
        headers: {
          Accept:
            format === "marcxml"
              ? "application/marcxml+xml"
              : "application/marc",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Export failed: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();
      const fileName = this.generateFileName(format, size);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback to opening in new window
      this.handleStandardExport(format, size, currentQueryState, exportBaseUrl);
    }
  };

  // Handle standard export formats (CSV, PDF)
  handleStandardExport = (format, size, currentQueryState, exportBaseUrl) => {
    const newQueryState = { ...currentQueryState, size };
    const requestSerializer = new InvenioRequestSerializer();
    const queryString = requestSerializer.serialize(newQueryState);
    const params = Qs.parse(queryString);
    params["page"] = 1;
    params["format"] = format;

    const args = Qs.stringify(params, { arrayFormat: "repeat", encode: false });
    const exportUrl = `${exportBaseUrl}?${args}`;
    window.open(exportUrl, "_blank");
  };

  onExportClick = (format, size) => {
    const { currentQueryState, exportBaseUrl, customOnExportClick } =
      this.props;

    if (customOnExportClick) {
      customOnExportClick(format, size, currentQueryState);
      return;
    }

    // Handle different export formats
    if (format === "marcxml-styled") {
      this.handleStyledMARC21Export(currentQueryState, size, exportBaseUrl);
    } else if (format === "marcxml" || format === "marc21") {
      this.handleMARC21Export(format, size, currentQueryState, exportBaseUrl);
    } else {
      this.handleStandardExport(format, size, currentQueryState, exportBaseUrl);
    }
  };

  render() {
    const { isLoading, supportedFormats } = this.props;
    const ExportDialogWithSearchState = withSearchState(ExportDialog);
    return (
      <ExportDialogWithSearchState
        onExportClick={this.onExportClick}
        isLoading={isLoading}
        supportedFormats={supportedFormats}
      />
    );
  }
}

ExportSearchResultsWithState.propTypes = {
  exportBaseUrl: PropTypes.string.isRequired,
  currentQueryState: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  customOnExportClick: PropTypes.func,
  supportedFormats: PropTypes.arrayOf(PropTypes.string),
};

ExportSearchResultsWithState.defaultProps = {
  isLoading: false,
  supportedFormats: ["csv", "pdf", "marc21"],
};

class ExportReactSearchKitResults extends Component {
  render() {
    const { exportBaseUrl, isLoading, onExportClick, supportedFormats } =
      this.props;
    const CmpWithState = withSearchState(ExportSearchResultsWithState);
    return (
      <CmpWithState
        exportBaseUrl={exportBaseUrl}
        isLoading={isLoading}
        customOnExportClick={onExportClick}
        supportedFormats={supportedFormats}
      />
    );
  }
}

ExportReactSearchKitResults.propTypes = {
  exportBaseUrl: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  onExportClick: PropTypes.func,
  supportedFormats: PropTypes.arrayOf(PropTypes.string),
};

ExportReactSearchKitResults.defaultProps = {
  isLoading: false,
  supportedFormats: ["csv", "pdf", "marc21"], // Default: all formats
};

class ExportSearchResults extends Component {
  render() {
    const { onExportClick, isLoading, supportedFormats } = this.props;
    return (
      <ExportDialog
        onExportClick={onExportClick}
        isLoading={isLoading}
        supportedFormats={supportedFormats}
      />
    );
  }
}

ExportSearchResults.propTypes = {
  onExportClick: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  supportedFormats: PropTypes.arrayOf(PropTypes.string),
};

ExportSearchResults.defaultProps = {
  isLoading: false,
  supportedFormats: ["csv", "pdf", "marc21"],
};

export { ExportReactSearchKitResults, ExportSearchResults };
